import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Blocks, Power } from 'lucide-react'

import { ApiError } from '@/api/client'
import { modulesApi } from '@/api/endpoints'
import type { AppModule } from '@/api/types'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert, EmptyState } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { SkeletonRows } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/Toast'
import { MODULES_QUERY_KEY, sortModules } from '@/hooks/useModules'

export function ModulesPage() {
  const queryClient = useQueryClient()
  const { notifySuccess, notifyError } = useToast()

  const query = useQuery({ queryKey: MODULES_QUERY_KEY, queryFn: () => modulesApi.list() })

  const toggle = useMutation({
    mutationFn: (modul: AppModule) => modulesApi.setEnabled(modul.slug, !modul.is_enabled),
    onSuccess: async (_, modul) => {
      await queryClient.invalidateQueries({ queryKey: MODULES_QUERY_KEY })
      notifySuccess(
        modul.is_enabled
          ? `Moduł „${modul.name}” został wyłączony.`
          : `Moduł „${modul.name}” został włączony.`,
      )
    },
    onError: (caught: unknown) => {
      // Komunikat z backendu mówi wprost, co włączyć lub wyłączyć najpierw —
      // własny tekst byłby uboższy.
      notifyError(caught instanceof ApiError ? caught.message : 'Nie udało się zmienić modułu.')
    },
  })

  const items = sortModules(query.data ?? [])

  return (
    <>
      <PageHeader
        title="Moduły"
        description="Zakres funkcji, z których korzysta szkoła. Wyłączona sekcja znika z menu i przestaje odpowiadać."
        breadcrumb={['System', 'Moduły']}
      />

      {query.isError && (
        <Alert tone="error" className="mb-6">
          Nie udało się wczytać listy modułów.
        </Alert>
      )}

      <Alert tone="info" className="mb-6">
        Moduły zależą od siebie: grafik potrzebuje sal i uczniów, obecności — grafiku, a rozliczenia
        — obecności. Dlatego wyłączać trzeba od góry, a włączać od podstaw.
      </Alert>

      <Card>
        {query.isPending ? (
          <SkeletonRows />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Blocks size={28} />}
            title="Brak modułów"
            description="Katalog modułów zakłada migracja bazy danych."
          />
        ) : (
          <ul className="divide-y divide-outline/10">
            {items.map((modul) => (
              <li
                key={modul.slug}
                className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:gap-6"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-bold text-on-surface">{modul.name}</p>
                    <Badge tone={modul.is_enabled ? 'success' : 'neutral'}>
                      {modul.is_enabled ? 'Włączony' : 'Wyłączony'}
                    </Badge>
                  </div>
                  {modul.description && (
                    <p className="mt-1 text-sm text-on-surface-variant">{modul.description}</p>
                  )}
                </div>

                <Button
                  variant={modul.is_enabled ? 'ghost' : 'primary'}
                  icon={<Power size={16} />}
                  isLoading={toggle.isPending && toggle.variables?.slug === modul.slug}
                  onClick={() => toggle.mutate(modul)}
                  className="shrink-0"
                >
                  {modul.is_enabled ? 'Wyłącz' : 'Włącz'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  )
}
